import { type TFunction } from 'i18next'
import { z } from 'zod'

import { MAX_TARGET_DATES } from '~/pages/WorkflowCases/constants/targetDates'

/**
 * N×M 一括作成フォームの値。
 *
 * `requiresBgCode` は選択中の雛形の derived field
 * (`WorkflowTemplate.requires_bg_code`) をフォーム状態へ写したもので、ユーザーが
 * 直接編集する入力ではない。BG コードの必須判定が「雛形のステップ構成から導出
 * される」ため、schema 側もこの値を見ないと BG 軸・エリア軸のどちらで検証すべきか
 * 決められない (workflow-bg-code-derivation design)。
 */
export type CreateCasesFormValues = {
  workflowTemplateId: string
  requiresBgCode: boolean
  bgCodes: string[]
  targetDates: string[]
}

/**
 * N×M 一括作成フォームのスキーマ (spec #1515/AC-003b)。
 * - name / note は削除 (spec B1/B2)
 * - bg_codes は **BG 必須と導出される雛形のときだけ** 1 件以上必須
 *   (BG 不要雛形はエリア軸で作成するため BG を送らない、spec AC-003j)
 * - area_codes はフォーム状態ではなくコンポーネントの useState が保持する
 *   (proto enum の配列を直接扱うため。BG 必須モードでは絞り込み条件、
 *   BG 不要モードでは作成軸として使う)
 * - target_dates は 1 件以上必須 (複数選択)。上限 `MAX_TARGET_DATES` (= proto
 *   `target_dates.max_items = 31`) も schema 側で defense-in-depth として検証する
 *   (`TargetDateCalendar` の UI 上限だけでは、雛形選択後の programmatic set 等の
 *   外部経路で 32 件以上が入った場合に submit ボタンが enabled のまま
 *   protovalidate の `InvalidArgument` で失敗する dead-end になり得た、
 *   PR #4723 レビュー指摘対応)
 * - assignee_member_id (ケース単位) は死に配線として撤去済み (workflow-substep-assignee design:
 *   spec AC-005 注釈「担当者はサブステップ単位」に伴い WorkflowCase 単位の担当者は廃止。
 *   proto field も reserved 化されている。AC-10)
 * - submit 時に "YYYY-MM-DD" 文字列を {year, month, day} に parse する。
 */
export function createCasesSchema(
  t: TFunction,
): z.ZodType<CreateCasesFormValues, CreateCasesFormValues> {
  return z
    .object({
      // 未選択の初期値は undefined で、型エラーが min(1) より先に出るため、
      // 型エラーにも同じ文言を当てる (AC-WFW-003)。
      workflowTemplateId: z
        .string({
          error: t('page:workflowCases.form.validationTemplateRequired'),
        })
        .min(1, t('page:workflowCases.form.validationTemplateRequired')),
      requiresBgCode: z.boolean(),
      bgCodes: z.array(z.string().min(1)),
      // 空の行は `TargetDateFields` が落として渡すので、 要素ごとの検証は持たない
      // (spec workflow.md §1 機能詳細「対象日入力」の空の行)。
      targetDates: z
        .array(z.string())
        .min(1, t('page:workflowCases.form.validationTargetDatesRequired'))
        .max(
          MAX_TARGET_DATES,
          t('page:workflowCases.form.validationTargetDatesMax'),
        ),
    })
    .superRefine((values, ctx) => {
      if (values.requiresBgCode && values.bgCodes.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['bgCodes'],
          message: t('page:workflowCases.form.validationBgCodesRequired'),
        })
      }
    })
}
